import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '@/utils/axios';
import { Mail, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const { email } = location.state || {};

  useEffect(() => {
    if (!email) {
      toast.error('No verification info found. Please register again.');
      navigate('/auth/register');
    }
  }, [email, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/auth/verify-otp', { email, otp: otpCode });
      localStorage.setItem('access_token', response.data.access_token);
      toast.success('Email verified! Analyzing your website...');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post('/auth/resend-otp', { email });
      toast.success('A new code has been sent to your email');
      setOtpCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4" data-testid="verify-otp-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Verify your email</h1>
            <p className="text-slate-600">We sent a 6-digit code to</p>
            <p className="text-slate-900 font-semibold flex items-center justify-center gap-2 mt-1">
              <Mail size={16} className="text-blue-600" /> {email}
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2 text-center">
                Enter verification code
              </label>
              <input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-3xl tracking-[0.5em] font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-2 text-center">The code expires in 10 minutes.</p>
            </div>

            <button
              data-testid="verify-otp-button"
              type="submit"
              disabled={loading || otpCode.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (<><Loader2 className="animate-spin" size={20} /> Verifying...</>) : 'Verify & Continue'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600 mb-1">Didn't receive the code?</p>
            <button
              data-testid="resend-otp-button"
              onClick={handleResend}
              disabled={resending}
              className="text-blue-600 font-semibold hover:text-blue-700 disabled:opacity-50"
            >
              {resending ? 'Resending...' : 'Resend code'}
            </button>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/auth/register')}
              className="text-sm text-slate-600 hover:text-slate-900"
              data-testid="back-to-register"
            >
              ← Back to sign up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
